import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, Cookie, Mail, MapPin, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-black pt-32 pb-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-green-400 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-5xl font-black text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-400">
              Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}
            </p>
          </div>

          {/* Content */}
          <Card className="bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
            <CardContent className="p-8 space-y-8 text-gray-300">
              
              {/* Introduzione */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Lock className="w-6 h-6 text-orange-500 mr-3" />
                  Introduzione
                </h2>
                <p className="leading-relaxed">
                  La presente Privacy Policy descrive le modalità di trattamento dei dati personali degli utenti 
                  che consultano il sito web di <strong className="text-white">Performove Training Lab</strong> 
                  (performoverimini.it).
                </p>
                <p className="leading-relaxed mt-4">
                  Il Titolare del trattamento è <strong className="text-white">Performove Training Lab</strong>, 
                  con sede in Viale Tripoli 216/B, 47921 Rimini, contattabile all'indirizzo email 
                  <a href="mailto:performove.rimini@gmail.com" className="text-orange-500 hover:text-orange-400"> performove.rimini@gmail.com</a>.
                </p>
              </section>

              {/* Dati Raccolti */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Dati Raccolti
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">1. Dati di Navigazione</h3>
                    <p className="leading-relaxed">
                      I sistemi informatici preposti al funzionamento del sito acquisiscono, nel corso del loro normale esercizio, 
                      alcuni dati personali la cui trasmissione è implicita nell'uso dei protocolli di comunicazione di Internet 
                      (es. indirizzi IP, tipo di browser, sistema operativo).
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">2. Dati Forniti Volontariamente</h3>
                    <p className="leading-relaxed mb-2">
                      Attraverso il form di contatto, raccogliamo i seguenti dati:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Nome e cognome</li>
                      <li>Indirizzo email</li>
                      <li>Numero di telefono</li>
                      <li>Messaggio/richiesta</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Finalità del Trattamento */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Finalità del Trattamento
                </h2>
                <p className="leading-relaxed mb-4">
                  I dati personali sono trattati per le seguenti finalità:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong className="text-white">Rispondere alle richieste:</strong> gestione delle richieste di contatto e prenotazione prove gratuite</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong className="text-white">Comunicazioni commerciali:</strong> invio di informazioni sui nostri servizi (con consenso)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong className="text-white">Miglioramento del servizio:</strong> analisi statistiche anonime per migliorare il sito</span>
                  </li>
                </ul>
              </section>

              {/* Base Giuridica */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Base Giuridica del Trattamento
                </h2>
                <p className="leading-relaxed">
                  Il trattamento dei dati personali si basa su:
                </p>
                <ul className="space-y-2 mt-3">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong className="text-white">Consenso dell'interessato</strong> (art. 6, par. 1, lett. a, GDPR)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong className="text-white">Esecuzione di misure precontrattuali</strong> (art. 6, par. 1, lett. b, GDPR)</span>
                  </li>
                </ul>
              </section>

              {/* Cookie */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Cookie className="w-6 h-6 text-orange-500 mr-3" />
                  Cookie Utilizzati
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Cookie Tecnici</h3>
                    <p className="leading-relaxed">
                      Utilizziamo cookie tecnici necessari per il corretto funzionamento del sito. 
                      Questi cookie non richiedono consenso.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Cookie di Terze Parti</h3>
                    <p className="leading-relaxed">
                      Il sito integra Google Maps per la visualizzazione della posizione. 
                      Consulta la <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400 underline">Privacy Policy di Google</a>.
                    </p>
                  </div>
                </div>
              </section>

              {/* Conservazione */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Conservazione dei Dati
                </h2>
                <p className="leading-relaxed">
                  I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti:
                </p>
                <ul className="space-y-2 mt-3">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong className="text-white">Richieste di contatto:</strong> 24 mesi dalla raccolta</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong className="text-white">Marketing:</strong> fino a revoca del consenso</span>
                  </li>
                </ul>
              </section>

              {/* Diritti */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  I Tuoi Diritti
                </h2>
                <p className="leading-relaxed mb-4">
                  In qualità di interessato, hai diritto a:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-bold text-white mb-2">✓ Accesso</h4>
                    <p className="text-sm">Ottenere conferma dell'esistenza dei tuoi dati</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-bold text-white mb-2">✓ Rettifica</h4>
                    <p className="text-sm">Correggere dati inesatti o incompleti</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-bold text-white mb-2">✓ Cancellazione</h4>
                    <p className="text-sm">Richiedere la rimozione dei tuoi dati</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-bold text-white mb-2">✓ Opposizione</h4>
                    <p className="text-sm">Opporti al trattamento per finalità di marketing</p>
                  </div>
                </div>
                <p className="leading-relaxed mt-4">
                  Per esercitare i tuoi diritti, contattaci a:{' '}
                  <a href="mailto:performove.rimini@gmail.com" className="text-orange-500 hover:text-orange-400 underline">
                    performove.rimini@gmail.com
                  </a>
                </p>
              </section>

              {/* Sicurezza */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Sicurezza dei Dati
                </h2>
                <p className="leading-relaxed">
                  Adottiamo misure di sicurezza tecniche e organizzative adeguate per proteggere i dati personali 
                  da accessi non autorizzati, perdita, distruzione o divulgazione. Il sito utilizza protocollo HTTPS 
                  per la cifratura delle comunicazioni.
                </p>
              </section>

              {/* Modifiche */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Modifiche alla Privacy Policy
                </h2>
                <p className="leading-relaxed">
                  Ci riserviamo il diritto di modificare la presente Privacy Policy in qualsiasi momento. 
                  Le modifiche saranno pubblicate su questa pagina con indicazione della data di ultimo aggiornamento.
                </p>
              </section>

              {/* Contatti */}
              <section className="bg-gradient-to-r from-orange-500/10 to-green-400/10 p-6 rounded-lg border border-orange-500/20">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Mail className="w-6 h-6 text-orange-500 mr-3" />
                  Contatti
                </h2>
                <div className="space-y-2">
                  <p className="flex items-center">
                    <MapPin className="w-4 h-4 text-orange-500 mr-2" />
                    <span>Viale Tripoli 216/B, 47921 Rimini</span>
                  </p>
                  <p className="flex items-center">
                    <Mail className="w-4 h-4 text-orange-500 mr-2" />
                    <a href="mailto:performove.rimini@gmail.com" className="text-orange-500 hover:text-orange-400">
                      performove.rimini@gmail.com
                    </a>
                  </p>
                  <p className="flex items-center">
                    <Phone className="w-4 h-4 text-orange-500 mr-2" />
                    <a href="tel:347-4955028" className="text-orange-500 hover:text-orange-400">
                      347-4955028
                    </a>
                  </p>
                </div>
              </section>

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
